import React, { useState } from "react";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { Spinner, SkeletonRows } from "../../Components/Common/LoadingStates";
import { getPaybisTransactionList } from "../../helpers/fakebackend_helper";
import { useCachedRequest } from "../../helpers/cachedFetch";

const parseJson = (val) => {
  if (val == null || val === "") return null;
  if (typeof val === "object") return val;
  try {
    return typeof val === "string" ? JSON.parse(val) : val;
  } catch {
    return null;
  }
};

const StatusBadge = ({ status }) => {
  const s = (status || "").toLowerCase();
  // Match the backend's isCompletedPaybisStatus (/completed$/i) so suffixed
  // statuses like paymentCompleted / payoutCompleted also show as completed.
  const isCompleted = /completed$/.test(s) || s === "complete";
  const isFailed = s === "failed" || s === "cancelled" || s === "canceled";
  const isPending = s === "created" || s === "pending" || s === "processing";
  const bg = isCompleted
    ? "bg-green-500/16 text-green-300"
    : isFailed
    ? "bg-red-500/16 text-red-300"
    : isPending
    ? "bg-amber-500/16 text-amber-300"
    : "bg-gray-700 text-gray-300";
  return (
    <span className={`px-2 py-1 text-xs font-normal rounded ${bg}`}>
      {status || "-"}
    </span>
  );
};

const PaybisTransactions = ({ embedded = false } = {}) => {
  document.title = "Paybis (On/Off Ramp) | BiorGov - BiorGov Admin & Dashboard";

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const req = useCachedRequest({
    key: `paybis-tx:list:${page}:${pageSize}:${statusFilter.trim()}:${dateFrom}:${dateTo}`,
    fetcher: () => {
      const params = {
        limit: pageSize,
        offset: (page - 1) * pageSize,
        sort_by: "created_at",
        sort_order: "DESC",
      };
      if (statusFilter.trim()) params.status = statusFilter.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      // axios interceptor already unwraps `.data` — `res` IS the body.
      return getPaybisTransactionList(params);
    },
    deps: [page, pageSize, statusFilter, dateFrom, dateTo],
  });
  const rows = req.data?.transactions || [];
  const total = req.data?.pagination?.total ?? rows.length;
  const loading = req.loading;
  const error = req.error ? (typeof req.error === "string" ? req.error : req.error?.message || "Failed to load Paybis transactions") : "";

  return (
    <div className={embedded ? "" : "min-h-screen"}>
      <div className={embedded ? "w-full min-w-0" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"}>
        {!embedded && <BreadCrumb title="Paybis (On/Off Ramp)" pageTitle="Transactions" />}

        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-sm text-gray-400">
            All Paybis on/off ramp transactions from the database.
          </p>
          <div className="flex flex-wrap gap-2">
            <input
              type="text"
              placeholder="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 text-sm border border-gray-700 rounded-md bg-gray-800 text-gray-100 w-32"
            />
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 text-sm border border-gray-700 rounded-md bg-gray-800 text-gray-100"
            />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 text-sm border border-gray-700 rounded-md bg-gray-800 text-gray-100"
            />
            <button
              type="button"
              onClick={() => req.refetch()}
              disabled={loading}
              className="px-4 py-2 text-sm font-normal text-gray-300 bg-gray-800 border border-gray-700 rounded-md hover:bg-bl-accent/10 disabled:opacity-50"
            >
              {loading ? "Loading…" : "Refresh"}
            </button>
          </div>
        </div>

        <div className="bl-glass overflow-hidden">
          {error && (
            <div className="p-4 bg-red-500/10 border-b border-red-500/30 text-sm text-red-400">
              {error}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-bl-accent/5">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Request ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Event</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Amount From</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Amount To</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Payout / Chain</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Invoice / Flow</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Created</th>
                </tr>
              </thead>
              <tbody className="bg-gray-800 divide-y divide-gray-700">
                {loading && <SkeletonRows rows={6} cols={9} />}
                {!loading && rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-sm text-gray-400">
                      No Paybis transactions found
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => {
                    const amountFrom = parseJson(r.amount_from);
                    const amountTo = parseJson(r.amount_to);
                    const payout = parseJson(r.payout);
                    const transaction = parseJson(r.transaction);
                    const fromStr = amountFrom
                      ? `${amountFrom.amount ?? ""} ${amountFrom.currency ?? ""}`.trim()
                      : "-";
                    const toStr = amountTo
                      ? `${amountTo.amount ?? ""} ${amountTo.currency ?? ""}`.trim()
                      : "-";
                    const payoutName = payout?.name || payout?.id || "-";
                    const payoutLink = payout?.explorer_link;
                    const invoice = transaction?.invoice ?? "-";
                    const flow = transaction?.flow ?? "-";
                    const created = r.created_at
                      ? new Date(r.created_at).toLocaleString()
                      : "-";
                    return (
                      <tr key={r.id} className="hover:bg-bl-accent/10/50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-100">{r.id}</td>
                        <td className="px-4 py-3 text-sm text-gray-300 font-mono">
                          {r.request_id ? `${r.request_id.slice(0, 8)}...` : "-"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">{r.event || "-"}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <StatusBadge status={r.transaction_status} />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-100">{fromStr}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-100">{toStr}</td>
                        <td className="px-4 py-3 text-sm text-gray-300">
                          {payoutLink ? (
                            <a href={payoutLink} target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:underline">
                              {payoutName}
                            </a>
                          ) : (
                            payoutName
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-300">
                          {invoice !== "-" ? `${invoice} / ${flow}` : flow !== "-" ? flow : "-"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-400">{created}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 px-4 py-3 border-t border-gray-700">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-400">Per page</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="px-2 py-1 text-sm border border-gray-700 rounded bg-gray-800 text-gray-100"
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div className="text-sm text-gray-400">
              Page {page} of {Math.max(1, Math.ceil(total / pageSize))} • {total} total
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 text-sm border border-gray-700 rounded-md bg-gray-800 text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bl-accent/10"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={loading || page >= Math.max(1, Math.ceil(total / pageSize))}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-sm border border-gray-700 rounded-md bg-gray-800 text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bl-accent/10"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaybisTransactions;
